<template>
  <card-dialog
    v-model="modalVisible"
    max-width="600px"
    :title="coachForm.id ? $t('edit_coach.dialog.header') : $t('new_coach.dialog.header')"
  >
    <v-tabs
      v-if="coachForm.user_id"
      v-model="view"
    >
      <v-tab
        href="#main"
        class="text-none"
      >
        {{ $t('edit_coach.dialog.tabs.main_details') }}
      </v-tab>
      <v-tab
        href="#permissions"
        class="text-none"
      >
        {{ $t('edit_coach.dialog.tabs.access_control') }}
      </v-tab>
    </v-tabs>
    <v-divider />
    <v-card-text
      class="pa-6"
    >
      <validation-observer ref="validation">
        <div v-if="view === 'main'">
          <subtitle class="mb-2">
            {{ $t('new_coach.profile.title') }}
          </subtitle>

          <v-row no-gutters>
            <v-col class="pr-2">
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="coachForm.first_name"
                  :error-messages="errors"
                  :label="$t('text_input.data.first_name.title')"
                  outlined
                  dense
                />
              </validation-provider>
            </v-col>
            <v-col class="pl-2">
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="coachForm.last_name"
                  :error-messages="errors"
                  :label="$t('text_input.data.last_name.title')"
                  outlined
                  dense
                />
              </validation-provider>
            </v-col>
          </v-row>

          <validation-provider
            v-slot="{ errors }"
            :rules="{ required: true, unique_username: { user_id: coachForm.user_id || undefined }}"
          >
            <v-text-field
              v-model="coachForm.username"
              :error-messages="errors"
              :label="$t('text_input.user.username.title')"
              :hint="$t('text_input.staff_username.hint')"
              outlined
              dense
            />
          </validation-provider>

          <validation-provider
            v-slot="{ errors }"
            :rules="{ required: true, email: true, unique_email: { user_id: coachForm.user_id || undefined }}"
          >
            <v-text-field
              v-model="coachForm.email"
              :error-messages="errors"
              :label="$t('text_input.user.email.title')"
              :hint="!coachForm.id ? $t('text_input.staff_email.hint') : undefined"
              outlined
              dense
            />
          </validation-provider>

          <div v-if="!coachForm.id">
            <v-switch
              v-model="manualPassword"
              :label="$t('switch.set_password_manually')"
              persistent-hint
              class="mt-0"
              :hint="manualPassword ? $t('switch.set_password_manually.player.no_instructions') : $t('switch.set_password_manually.staff.instructions')"
            />

            <div
              v-if="manualPassword"
              class="mt-5"
            >
              <validation-provider
                v-slot="{ errors }"
                rules="required|password_policy"
                vid="password"
              >
                <password-field
                  v-model="coachForm.password"
                  :error-messages="errors"
                  :label="$t('text_input.login.password.title')"
                  outlined
                  type="password"
                  append-icon="fa-light fa-key"
                  dense
                />
              </validation-provider>

              <validation-provider
                v-slot="{ errors }"
                rules="required|confirmed:password"
                mode="eager"
              >
                <password-field
                  v-model="coachForm.password_repeat"
                  :error-messages="errors"
                  :label="$t('text_input.user.repeat_password.title')"
                  outlined
                  type="password"
                  append-icon="fa-light fa-key"
                  dense
                />
              </validation-provider>
            </div>
          </div>

          <validation-provider
            v-slot="{ errors }"
            ref="imageErrors"
          >
            <div class="text-body-2 v-label mb-2 mt-4">
              {{ $t('media_input.data.photo.title') }}
            </div>
            <image-crop
              ref="coachImage"
              v-model="coachForm.photo_base_64"
              :quality="2"
              :width="200"
              :height="200"
              prevent-white-space
              :placeholder="$t('media_input.data.photo.placeholder')"
              accept="image/png,image/jpeg"
              :placeholder-font-size="12"
              :file-size-limit="2000000"
              :initial-image="coachForm.photo_url"
              @errors="$refs.imageErrors.setErrors($event)"
            />
            <div
              v-if="errors.length > 0"
              class="error--text caption"
            >
              {{ errors[0] }}
            </div>
          </validation-provider>

          <subtitle class="mt-5">
            {{ $t('card_title.personal_info') }}
          </subtitle>

          <div class="text-body-2 v-label mb-2">
            {{ $t('text_input.data.date_of_birth.title') }}
          </div>

          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <dob-input
              v-model="coachForm.birth_date"
              dense
              :unit="user.organisation_settings.unit_system"
              :error-messages="errors"
            />
          </validation-provider>

          <v-text-field
            v-model="coachForm.address"
            outlined
            dense
            :label="$t('text_input.data.address.title')"
          />

          <v-text-field
            v-model="coachForm.phone"
            outlined
            dense
            :label="$t('text_input.data.phone_number.title')"
            placeholder="+385 99 876 5432"
          />

          <subtitle class="mt-2">
            {{ $t('new_coach.details.title') }}
          </subtitle>

          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-select
              v-model="coachForm.coachTypeId"
              name="teamId"
              :items="coachTypes.data"
              :loading="coachTypes.loading"
              item-value="id"
              item-text="name"
              outlined
              dense
              :error-messages="errors"
              :label="$t('dropdown.coach_type.title')"
            />
          </validation-provider>

          <validation-provider
            v-if="!hasNoPlayers"
            v-slot="{ errors }"
            :rules="coachForm.supervisorTeamIds.length === 0 ? 'required' : undefined"
          >
            <v-select
              v-model="coachForm.teamIds"
              multiple
              name="teamId"
              :items="teamSelectItems"
              item-value="id"
              item-text="name"
              chips
              small-chips
              outlined
              dense
              :error-messages="errors"
              :label="$t('dropdown.assigned_to_teams.title')"
            />
          </validation-provider>

          <validation-provider
            v-if="!hasNoPlayers"
            v-slot="{ errors }"
            :rules="coachForm.teamIds.length === 0 ? 'required' : undefined"
          >
            <v-select
              v-model="coachForm.supervisorTeamIds"
              multiple
              name="supervisorTeamId"
              :items="teamSelectItems"
              item-value="id"
              item-text="name"
              chips
              small-chips
              outlined
              dense
              :error-messages="errors"
              :label="$t('dropdown.supervisor_teams.title')"
            />
          </validation-provider>

          <div v-if="!hasNoPlayers">
            <div
              v-if="hasRegularReports"
              class="mb-8 mt-2"
            >
              <subtitle class="mb-4">
                {{ $t('new_coach.report.title') }}
              </subtitle>

              <v-row
                align="center"
              >
                <v-col class="body-2">
                  {{ $t('switch.receive_daily_morning_report') }}
                </v-col>
                <v-col cols="4">
                  <v-switch
                    v-model="coachForm.settings.receive_daily_morning_report"
                    flat
                    hide-details
                    class="ma-0"
                  />
                </v-col>
              </v-row>
              <v-row
                align="center"
                :class="{ 'text--disabled': !coachForm.settings.receive_daily_morning_report}"
              >
                <v-col class="body-2">
                  {{ $t('switch.daily_morning_report_time') }}
                </v-col>
                <v-col cols="4">
                  <v-select
                    v-model="coachForm.settings.daily_morning_report_time"
                    outlined
                    dense
                    :disabled="!coachForm.settings.receive_daily_morning_report"
                    :items="timeItems.slice(10, 48)"
                    hide-details
                  />
                </v-col>
              </v-row>

              <v-row
                align="center"
              >
                <v-col class="body-2">
                  {{ $t('switch.receive_daily_evening_report') }}
                </v-col>
                <v-col cols="4">
                  <v-switch
                    v-model="coachForm.settings.receive_daily_evening_report"
                    flat
                    hide-details
                    class="ma-0"
                  />
                </v-col>
              </v-row>
              <v-row
                align="center"
                :class="{ 'text--disabled': !coachForm.settings.receive_daily_evening_report}"
              >
                <v-col class="body-2">
                  {{ $t('switch.daily_evening_report_time') }}
                </v-col>
                <v-col cols="4">
                  <v-select
                    v-model="coachForm.settings.daily_evening_report_time"
                    outlined
                    dense
                    :disabled="!coachForm.settings.receive_daily_evening_report"
                    :items="timeItems.slice(34, 48)"
                    hide-details
                  />
                </v-col>
              </v-row>

              <v-row
                align="center"
              >
                <v-col class="body-2">
                  {{ $t('switch.receive_event_reports') }}
                </v-col>
                <v-col cols="4">
                  <v-switch
                    v-model="coachForm.settings.receive_event_reports"
                    flat
                    hide-details
                    class="ma-0"
                  />
                </v-col>
              </v-row>
              <v-row
                align="center"
                :class="{ 'text--disabled': !coachForm.settings.receive_event_reports}"
              >
                <v-col class="body-2">
                  {{ $t('dropdown.how_soon_after.title') }}
                </v-col>
                <v-col cols="4">
                  <v-select
                    v-model="coachForm.settings.event_report_minutes_after_event"
                    outlined
                    dense
                    :disabled="!coachForm.settings.receive_event_reports"
                    :items="[ { text: $t('how_soon_settings.after.30_mins'), value: 30 }, { text: $t('how_soon_settings.after.1_hour'), value: 60 }, { text: $t('how_soon_settings.after.1.5_hours'), value: 90 }, { text: $t('how_soon_settings.after.2_hours'), value: 120 }, { text: $t('how_soon_settings.after.3_hours'), value: 180 }, { text: $t('how_soon_settings.after.4_hours'), value: 240 }]"
                    hide-details
                  />
                </v-col>
              </v-row>

              <v-row
                align="center"
              >
                <v-col class="body-2">
                  {{ $t('switch.receive_weekly_report') }}
                </v-col>
                <v-col cols="4">
                  <v-switch
                    v-model="coachForm.settings.receive_weekly_report"
                    flat
                    hide-details
                    class="ma-0"
                  />
                </v-col>
              </v-row>
              <v-row
                align="center"
                :class="{ 'text--disabled': !coachForm.settings.receive_weekly_report}"
              >
                <v-col class="body-2">
                  {{ $t('dropdown.weekly_report_day.title') }}
                </v-col>
                <v-col cols="4">
                  <v-select
                    v-model="coachForm.settings.weekly_report_day"
                    outlined
                    dense
                    :disabled="!coachForm.settings.receive_weekly_report"
                    :items="dayItems"
                    hide-details
                  />
                </v-col>
              </v-row>
              <v-row
                align="center"
                :class="{ 'text--disabled': !coachForm.settings.receive_weekly_report}"
              >
                <v-col class="body-2">
                  {{ $t('dropdown.weekly_report_time.title') }}
                </v-col>
                <v-col cols="4">
                  <v-select
                    v-model="coachForm.settings.weekly_report_time"
                    outlined
                    dense
                    :disabled="!coachForm.settings.receive_weekly_report"
                    :items="timeItems.slice(10, 48)"
                    hide-details
                  />
                </v-col>
              </v-row>

              <v-row
                align="center"
              >
                <v-col class="body-2">
                  {{ $t('switch.receive_monthly_report') }}
                </v-col>
                <v-col cols="4">
                  <v-switch
                    v-model="coachForm.settings.receive_monthly_report"
                    flat
                    hide-details
                    class="ma-0"
                  />
                </v-col>
              </v-row>
              <v-row
                align="center"
                :class="{ 'text--disabled': !coachForm.settings.receive_monthly_report}"
              >
                <v-col class="body-2">
                  {{ $t('dropdown.monthly_report_time.title') }}
                </v-col>
                <v-col cols="4">
                  <v-select
                    v-model="coachForm.settings.monthly_report_time"
                    outlined
                    dense
                    :disabled="!coachForm.settings.receive_monthly_report"
                    :items="timeItems.slice(10, 48)"
                    hide-details
                  />
                </v-col>
              </v-row>
            </div>

            <subtitle
              v-if="hasItlModule || hasSmartAssistantModule"
              class="mt-2 mb-4"
            >
              {{ $t('new_coach.notification_settings.title') }}
            </subtitle>

            <v-row
              v-if="hasItlModule"
              align="center"
            >
              <v-col class="body-2">
                {{ $t('switch.receive_new_player_feedback') }}
              </v-col>
              <v-col cols="4">
                <v-switch
                  v-model="coachForm.settings.receive_new_feedback_submitted_notification"
                  flat
                  hide-details
                  class="ma-0"
                />
              </v-col>
            </v-row>

            <v-row
              v-if="hasSmartAssistantModule"
              align="center"
            >
              <v-col class="body-2">
                {{ $t("switch.receive_smart_assistant") }}
              </v-col>
              <v-col cols="4">
                <v-switch
                  v-model="coachForm.settings.receive_smart_assistant_notifications"
                  flat
                  hide-details
                  class="ma-0"
                />
              </v-col>
            </v-row>
          </div>

          <div
            v-if="coachForm.id && ((hasPolarIntegration && polarAuthUrl) || (hasGarminIntegration && garminAuthUrl) || hasWhoopIntegration)"
            class="mt-6"
          >
            <subtitle class="mb-2">
              {{ $t('text_input.integrations.title') }}
            </subtitle>

            <v-row
              v-if="hasPolarIntegration"
              align="center"
            >
              <v-col>
                <div>Polar One</div>
                <div
                  v-if="coachForm.polar_id"
                  class="text-caption success--text"
                >
                  <v-icon
                    left
                    x-small
                  >
                    fa fa-check
                  </v-icon>
                  {{ $t('card_title.connected') }}
                </div>
              </v-col>
              <v-col cols="auto">
                <v-btn
                  text
                  color="primary"
                  class="text-none"
                  :href="polarAuthUrl"
                >
                  {{ coachForm.polar_id ? $t("button.reconnect") : $t("button.connect_account") }}
                </v-btn>
              </v-col>
            </v-row>

            <v-row
              v-if="hasGarminIntegration"
              align="center"
            >
              <v-col>
                <div>Garmin Connect</div>
                <div
                  v-if="coachForm.garmin_id"
                  class="text-caption success--text"
                >
                  <v-icon
                    left
                    x-small
                  >
                    fa fa-check
                  </v-icon>
                  {{ $t('card_title.connected') }}
                </div>
              </v-col>
              <v-col cols="auto">
                <v-btn
                  text
                  color="primary"
                  class="text-none"
                  :href="garminAuthUrl"
                >
                  {{ coachForm.garmin_id ? $t("button.reconnect"): $t("button.connect_account") }}
                </v-btn>
              </v-col>
            </v-row>

            <v-row
              v-if="hasWhoopIntegration"
              align="center"
            >
              <v-col>
                <div>Whoop</div>
                <div
                  v-if="coachForm.whoop_id"
                  class="text-caption success--text"
                >
                  <v-icon
                    left
                    x-small
                  >
                    fa fa-check
                  </v-icon>
                  {{ $t('card_title.connected') }}
                </div>
                <div
                  v-else
                  class="text-caption grey--text"
                >
                  {{ $t('card_title.connect_from_mobile') }}
                </div>
              </v-col>
              <v-col cols="auto">
                <v-btn
                  v-if="coachForm.whoop_id"
                  text
                  color="primary"
                  class="text-none"
                  :loading="whoopDisconnecting"
                  @click="disconnectWhoop()"
                >
                  {{ $t('button.disconnect') }}
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </div>
        <div v-else-if="view === 'permissions'">
          <v-row
            no-gutters
            align="center"
            class="py-2 px-1"
          >
            <v-col>
              {{ $t('edit_coach.dialog.permissions.allow_admin') }}
            </v-col>
            <v-col cols="auto">
              <v-switch
                v-model="hasAdminAccess"
                class="pa-0 ma-0"
                hide-details
              />
            </v-col>
          </v-row>
          <v-row
            no-gutters
            align="center"
            class="py-2 px-1"
          >
            <v-col>
              {{ $t('edit_coach.dialog.permissions.allow_team_admin') }}
            </v-col>
            <v-col cols="auto">
              <v-switch
                v-model="hasTeamAdminAccess"
                class="pa-0 ma-0"
                hide-details
              />
            </v-col>
          </v-row>
          <v-divider class="my-2" />
          <v-row
            v-for="ability in abilities"
            :key="ability.id"
            no-gutters
            align="center"
            class="py-2 px-1"
          >
            <v-col>
              {{ ability.title }}
            </v-col>
            <v-col cols="auto">
              <v-switch
                v-model="ability.active"
                class="pa-0 ma-0"
                hide-details
              />
            </v-col>
          </v-row>
        </div>
      </validation-observer>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4">
        <v-btn
          v-if="coachForm.id && user.coach.id !== coachForm.id"
          class="mt-2"
          color="error"
          text
          small
          @click="deleteConfirmationDialogVisible = true"
        >
          {{ $t('button.delete') }}
        </v-btn>
        <v-spacer />
        <v-btn
          v-if="coachForm.user && !coachForm.user.is_setup_complete"
          outlined
          color="grey darken-2"
          class="text-none"
          :loading="mailResending"
          @click="resendInvitationEmail()"
        >
          {{ $t('button.resend') }}
        </v-btn>
        <v-btn
          color="primary"
          :loading="submitting"
          class="text-none"
          @click="submit()"
        >
          {{ $t('button.save_coach') }}
        </v-btn>
      </v-card-actions>
    </template>

    <delete-confirmation-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      @confirmed="deleteCoach()"
    />
  </card-dialog>
</template>

<script>
import CardDialog from '@/components/helpers/CardDialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import { AuthApiService, CoachApiService, CoachTypeApiService } from '@/data/services'
import { EventBus } from '@/plugins/bus'
import { cloneDeep } from 'lodash'
import Subtitle from '@/components/helpers/Subtitle'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog'
import DobInput from '@/components/helpers/DobInput'
import dayjs from 'dayjs'
import { UserApiService } from '@/data/services/UserApiService'
import ImageCrop from '@/components/inputs/ImageCrop.vue'
import { getDateTimeFormatForUser, toCamelCase } from '@/utils/helpers'
import PasswordField from '@/components/inputs/PasswordField.vue'

const emptySettings = {
  receive_daily_morning_report: true,
  daily_morning_report_time: '10:00',
  receive_daily_evening_report: true,
  daily_evening_report_time: '22:00',
  receive_weekly_report: true,
  weekly_report_day: 1,
  weekly_report_time: '08:00',
  receive_monthly_report: true,
  monthly_report_time: '08:00',
  receive_event_reports: true,
  event_report_minutes_after_event: 90,
  receive_new_feedback_submitted_notification: true,
  receive_smart_assistant_notifications: true
}

const emptyForm = {
  id: null,
  first_name: '',
  last_name: '',
  username: '',
  password: '',
  email: '',
  address: '',
  phone: '',
  dob: {
    day: '',
    month: '',
    year: ''
  },
  birth_date: null,
  coachTypeId: null,
  teamIds: [],
  supervisorTeamIds: [],
  settings: emptySettings,
  photo_base_64: null
}

export default {
  name: 'CoachFormDialog',
  components: { PasswordField, ImageCrop, DobInput, DeleteConfirmationDialog, Subtitle, CardDialog },
  mixins: [deleteConfirmationMixin],
  props: {
    coach: {
      type: Object,
      default: () => ({})
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      coachForm: cloneDeep(emptyForm),
      coachTypes: {
        data: [],
        loading: false
      },
      datePickerMenuVisible: false,
      submitting: false,
      manualPassword: false,
      timeItems: [],
      dayItems: [],
      mailResending: false,
      garminAuthUrl: null,
      whoopDisconnecting: false,
      view: 'main',
      abilities: [],
      hasAdminAccess: false,
      hasTeamAdminAccess: false
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    teamSelectItems() {
      return this.$store.getters[this.$storeGetters.APP_TEAMS]
    },
    polarAuthUrl() {
      if (!this.coachForm.polar_authorize_url) return undefined

      const state = JSON.stringify({
        coach_id: this.coachForm.id,
        redirect_uri: window.location.href
      })

      return this.coachForm.polar_authorize_url + '&state=' + btoa(state)
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    hasPolarIntegration() {
      return this.user.organisation_settings.integrations.some(i => i.key === 'polar')
    },
    hasGarminIntegration() {
      return this.user.organisation_settings.integrations.some(i => i.key === 'garmin')
    },
    hasWhoopIntegration() {
      return this.user.organisation_settings.integrations.some(i => i.key === 'whoop')
    },
    hasRegularReports() {
      return this.user.organisation_settings.has_regular_reports
    },
    hasNoPlayers() {
      return this.user.organisation_settings.has_no_players
    },
    hasItlModule() {
      return this.user.organisation_settings.has_itl_module
    },
    hasSmartAssistantModule() {
      return this.user.organisation_settings.has_smart_assistant_module
    }
  },
  watch: {
    visible(value) {
      if (!value) return

      this.fetchGarminAuthUrl()

      this.view = 'main'
    }
  },
  created() {
    const items = []

    let startTime = dayjs().startOf('day')
    const day = startTime.day()

    while (startTime.day() === day) {
      items.push({
        value: startTime.format('HH:mm'),
        text: startTime.format(getDateTimeFormatForUser('HH:mm'))
      })
      startTime = startTime.add(30, 'm')
    }

    this.timeItems = items

    for (let d = 1; d <= 3; d++) {
      this.dayItems.push({ value: d, text: dayjs().day(d).format('dddd') })
    }

    EventBus.$on(this.$events.OPEN_DIALOG_COACH_FORM, () => {
      this.coachForm = cloneDeep(emptyForm)
      this.modalVisible = true

      this.$nextTick(() => {
        this.$refs.validation.reset()
        this.$refs.coachImage.refresh()
      })

      this.fetchCoachTypes()
      this.fetchAllAbilities()
    })

    EventBus.$on(this.$events.EDIT_COACH, async (coach) => {
      try {
        const response = await CoachApiService.getCoachDetails(coach.id)
        const detailedCoach = response.data

        this.coachForm = { ...cloneDeep(emptyForm), ...cloneDeep(detailedCoach) }

        this.coachForm.teamIds = detailedCoach.teams.filter(t => t.is_coach).map(t => t.id)
        this.coachForm.supervisorTeamIds = detailedCoach.teams.filter(t => t.is_supervisor).map(t => t.id)
        this.coachForm.coachTypeId = detailedCoach.coach_type_id
        this.coachForm.settings = detailedCoach.settings || cloneDeep(emptySettings)

        this.modalVisible = true

        this.$nextTick(() => {
          this.$refs.coachImage.refresh()
        })

        await this.fetchCoachTypes()
        await this.fetchAllAbilities()
      } catch (e) {
      }
    })
  },
  methods: {
    async fetchCoachTypes() {
      try {
        this.coachTypes.loading = true

        const response = await CoachTypeApiService.getAll()

        this.coachTypes.data = response.data.map((coachType) => {
          return {
            ...coachType,
            name: this.$t(`coach_type.${toCamelCase(coachType.name)}`)
          }
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.coachTypes.loading = false
      }
    },
    async submit() {
      this.submitting = true

      if (!(await this.$refs.validation.validate())) {
        this.submitting = false
        return
      }

      await this.save()
    },
    async save() {
      try {
        const data = {
          id: this.coachForm.id,
          first_name: this.coachForm.first_name,
          last_name: this.coachForm.last_name,
          birth_date: this.coachForm.birth_date,
          description: this.coachForm.description,
          email: this.coachForm.email,
          username: this.coachForm.username,
          password: this.manualPassword ? this.coachForm.password : undefined,
          address: this.coachForm.address,
          phone: this.coachForm.phone,
          coach_type_id: this.coachForm.coachTypeId,
          team_ids: this.coachForm.teamIds,
          supervisor_team_ids: this.coachForm.supervisorTeamIds,
          settings: this.coachForm.settings,
          photo_base_64: this.coachForm.photo_base_64,
          is_admin: this.hasAdminAccess,
          is_team_admin: this.hasTeamAdminAccess,
          abilities: this.abilities.map(a => {
            return {
              name: a.name,
              active: a.active
            }
          })
        }

        let response

        if (this.coachForm.id) {
          response = await CoachApiService.update(data)
        } else {
          response = await CoachApiService.create(data)
        }

        if (this.coachForm.id) {
          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.coach_update.success'),
            color: 'success'
          })

          EventBus.$emit(this.$events.COACH_UPDATED, response.data)

          this.$store.dispatch(this.$storeActions.APP_FETCH_TEAMS)
        } else {
          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.coach_created.success'),
            color: 'success'
          })

          EventBus.$emit(this.$events.COACH_CREATED, response.data)
        }

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.coach_update.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async deleteCoach() {
      try {
        this.deleting = true

        await CoachApiService.delete(this.coachForm.id)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.coach_delete.success'),
          color: 'success'
        })

        this.modalVisible = false

        EventBus.$emit(this.$events.COACH_DELETED, this.coachForm)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.coach_delete.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.deleting = false
      }
    },
    async resendInvitationEmail() {
      if (!this.coachForm.user_id) return

      try {
        this.mailResending = true

        await UserApiService.resendInvitationEmail(this.coachForm.user_id)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.email_send.success'),
          color: 'success'
        })

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.email_send.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.mailResending = false
      }
    },
    async fetchGarminAuthUrl() {
      if (!this.hasGarminIntegration) return

      try {
        const response = await AuthApiService.getGarminAuthUrl({
          coach_id: this.coachForm.id,
          redirect_uri: window.location.href
        })

        this.garminAuthUrl = response.url
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_garmin.error'),
          color: 'error'
        })
        throw e
      }
    },
    async disconnectWhoop() {
      this.whoopDisconnecting = true
      try {
        await AuthApiService.deleteWhoopIntegration({
          coach_id: this.coachForm.id
        })
        this.coachForm.whoop_id = null
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.disconnect_whoop.success'),
          color: 'success'
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.disconnect_whoop.error'),
          color: 'error'
        })
      } finally {
        this.whoopDisconnecting = false
      }
    },
    async fetchAllAbilities() {
      try {
        const result = await UserApiService.getAllAbilities()

        this.abilities = result.map(ability => {
          ability.active = false
          ability.title = this.$t(`permissions.abilities.${ability.name}`)

          return ability
        })

        this.prepareAbilities()
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: 'Error loading permissions',
          color: 'error'
        })
        throw e
      }
    },
    prepareAbilities() {
      if (this.coachForm.id) {
        this.abilities = this.abilities.map(a => {
          a.active = this.coachForm.user.permission_rules[0].actions.indexOf(a.name) > -1

          return a
        })

        this.hasAdminAccess = this.coachForm.user.permission_rules[0].actions.indexOf('manage-organisation') > -1
        this.hasTeamAdminAccess = this.coachForm.user.permission_rules[0].actions.indexOf('manage-team-players') > -1

        return
      }

      this.abilities = this.abilities.map(a => {
        a.active = true

        return a
      })

      this.hasAdminAccess = false
    }
  }
}
</script>
